import type { HttpPort } from "~/lib/http";

export type MoodPeriod = "week" | "month" | "year" | "all";
export type MoodTrend = "rising" | "stable" | "falling" | "no-data";

interface MoodStudentValue {
  name: string;
  mood: number;
}

interface MoodHistoryDay {
  date: string;
  weekday: string;
  avg_mood: number | null;
  students: MoodStudentValue[];
  total_checkouts: number;
}

interface MoodHistoryResponse {
  period: MoodPeriod;
  data: MoodHistoryDay[];
  overall_avg: number | null;
  trend: MoodTrend;
}

interface MoodTrendValue {
  current: number | null;
  previous: number | null;
  trend: MoodTrend;
}

interface MoodTrendsResponse {
  day: MoodTrendValue;
  week: MoodTrendValue;
  month: MoodTrendValue;
  year: MoodTrendValue;
}

interface TodayResponse {
  total_students?: number;
  evening_count?: number;
}

export interface MoodRecommendation {
  id: string;
  week_start: string;
  trend: MoodTrend;
  tip_text: string;
  created_at: string;
  periodLabel: string;
}

export interface MoodChartSeries {
  label: string;
  data: Array<number | null>;
  color: string;
  isStudent: boolean;
}

export interface MoodLevel extends MoodTrendValue {
  label: "Heute" | "Woche" | "Monat" | "Jahr";
  color: string;
  trendLabel: string;
  delta: string;
}

export interface MoodDashboard {
  period: MoodPeriod;
  periodLabel: string;
  todayValue: number | null;
  todayCount: number | null;
  periodValue: number | null;
  levels: MoodLevel[];
  recommendation: {
    visible: boolean;
    label: string;
    color: string;
    text: string;
    standLabel: string;
  };
  recommendations: MoodRecommendation[];
  labels: string[];
  series: MoodChartSeries[];
}

const periodLabels: Record<MoodPeriod, string> = {
  week: "Ø diese Woche",
  month: "Ø dieser Monat",
  year: "Ø dieses Jahr",
  all: "Ø Gesamt"
};

const trendView: Record<MoodTrend, { color: string; label: string }> = {
  rising: { color: "#22c55e", label: "Steigend" },
  stable: { color: "#f59e0b", label: "Stabil" },
  falling: { color: "#ef4444", label: "Fallend" },
  "no-data": { color: "#9ca3af", label: "Keine Daten" }
};

const studentColors = ["#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FFEAA7", "#DDA0DD", "#98D8C8", "#F7DC6F", "#BB8FCE", "#82E0AA", "#F0B27A", "#85C1E9", "#F1948A", "#A9CCE3", "#A9DFBF", "#FAD7A0"];

export function moodEmoji(value: number | null) {
  const emojis: Record<number, string> = { 1: "😞", 2: "😕", 3: "😐", 4: "😊", 5: "😄", 6: "🤩" };
  return value == null ? "" : emojis[Math.round(value)] ?? "❓";
}

export function moodAxisTick(value: string | number) {
  const numeric = Number(value);
  const labels: Record<number, string> = { 1: "😢", 2: "😟", 3: "😐", 4: "🙂", 5: "😄", 6: "🤩" };
  return Number.isInteger(numeric) ? labels[numeric] ?? numeric : numeric;
}

function localDate(value: string) {
  return new Date(`${value.slice(0, 10)}T12:00:00`);
}

function localDateKey(value: Date) {
  return value.toLocaleDateString("sv-SE");
}

function weekNumber(value: string) {
  const date = localDate(value);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
  const weekOne = new Date(date.getFullYear(), 0, 4);
  return 1 + Math.round(((date.getTime() - weekOne.getTime()) / 86400000 - 3 + (weekOne.getDay() + 6) % 7) / 7);
}

function recommendationPeriodLabel(weekStart: string) {
  const start = localDate(weekStart);
  const friday = new Date(start);
  friday.setDate(start.getDate() + 4);
  const short = (value: Date) => `${String(value.getDate()).padStart(2, "0")}.${String(value.getMonth() + 1).padStart(2, "0")}.`;
  return `KW ${weekNumber(weekStart)} · ${short(start)} – ${short(friday)}`;
}

function tipFor(trend: MoodTrend, trends: MoodTrendsResponse) {
  const weekDelta = trends.week.current != null && trends.week.previous != null ? trends.week.current - trends.week.previous : null;
  const dayVsWeek = trends.day.current != null && trends.week.current != null ? trends.day.current - trends.week.current : null;
  const lowMood = trends.week.current != null && trends.week.current < 3;
  const bigDrop = weekDelta != null && weekDelta < -0.6;
  const midWeekRecovery = trends.week.trend === "falling" && trends.day.trend !== "falling" && dayVsWeek != null && dayVsWeek > 0.2;
  const strongRise = weekDelta != null && weekDelta > 0.6;

  if (trend === "falling") {
    if (midWeekRecovery) return "Die Stimmung fiel in der Wochenmitte — zum Ende hin erholt sie sich wieder. Ein gutes Zeichen! Frage trotzdem nach: Was hat in der Mitte der Woche belastet? Früh erkannte Muster lassen sich nächste Woche abfangen.";
    if (bigDrop && lowMood) return "Die Klassenstimmung ist diese Woche deutlich gefallen und liegt auf einem niedrigen Niveau. Plane bewusste Entlastung: eine offene Runde am Montag, wo jede:r sagen darf was sie/ihn beschäftigt, oder eine aktivitätsorientierte Einheit. Konkrete Frage: „Was hat euch diese Woche besonders belastet?“";
    if (bigDrop) return "Diese Woche zeigt einen deutlichen Stimmungsrückgang. Prüfe ob äußere Faktoren — Tests, Konflikte, Überforderung — die Wochenmitte geprägt haben. Ein Reset am Montag (z.B. anonyme Rückmeldung per Kummerkasten) kann helfen, die Ursachen besser zu verstehen.";
    return "Die Stimmung sinkt leicht. Ein kurzes offenes Gespräch am Morgen kann helfen — frage einfach: „Wie geht es euch heute wirklich?“";
  }
  if (trend === "rising") return strongRise
    ? "Starker Aufwärtstrend! Die Klasse hat sich diese Woche deutlich positiv entwickelt. Sichtbar feiern, Momentum nutzen: Lass die Klasse selbst benennen, was gut gelaufen ist — das stärkt das Bewusstsein für eigene Wirksamkeit."
    : "Die Klasse ist im Flow — nutze den Schwung für neue Herausforderungen und gib der Klasse Raum, sich selbst zu feiern.";
  if (trend === "stable") return lowMood
    ? "Die Stimmung ist stabil, aber auf niedrigem Niveau. Stabile Tiefs schleichen sich leicht ein. Beobachte einzelne Schüler:innen genauer und setze kleine positive Impulse — eine kurze Würdigungs-Runde am Morgen wirkt oft mehr als erwartet."
    : "Gute, stabile Stimmung. Sorge für Abwechslung im Alltag damit das Niveau gehalten wird.";
  return "Noch nicht genug Daten für eine Einschätzung.";
}

function chartSeries(days: MoodHistoryDay[]): MoodChartSeries[] {
  const names = [...new Set(days.flatMap((day) => day.students.map((student) => student.name)))].sort();
  const students = names.map((name, index) => ({
    label: name,
    data: days.map((day) => day.students.find((student) => student.name === name)?.mood ?? null),
    color: studentColors[index % studentColors.length],
    isStudent: true
  }));
  return [...students, {
    label: "⌀ Klasse",
    data: days.map((day) => day.avg_mood),
    color: "#6C63FF",
    isStudent: false
  }];
}

export function createMoodModule(http: HttpPort, token: string, now = () => new Date()) {
  return {
    async load(classId: string, period: MoodPeriod, totalStudents: number): Promise<MoodDashboard> {
      const current = now();
      const [history, trends, initialRecommendations] = await Promise.all([
        http.request<MoodHistoryResponse>({ path: `/classes/${classId}/mood-history?period=${period}`, token }),
        http.request<MoodTrendsResponse>({ path: `/classes/${classId}/mood-trends`, token }),
        http.request<MoodRecommendation[]>({ path: `/classes/${classId}/weekly-recommendations`, token })
      ]);
      const todayKey = localDateKey(current);
      const today = history.data.find((day) => day.date === todayKey);
      const validDays = history.data.filter((day) => day.avg_mood != null);
      const calculatedPeriod = validDays.length
        ? Math.round(validDays.reduce((sum, day) => sum + (day.avg_mood ?? 0), 0) / validDays.length * 10) / 10
        : null;
      const mappedTrend = period === "all" ? null : trends[period === "week" ? "week" : period === "month" ? "month" : "year"].current;
      const weekday = current.getDay();
      const fridayEvening = weekday === 5 && current.getHours() >= 15;
      const weekend = weekday === 0 || weekday === 6;
      let checkoutReady = weekend;
      if (fridayEvening) {
        const todayData = await http.request<TodayResponse>({ path: `/classes/${classId}/today`, token }).catch((): TodayResponse => ({}));
        const total = todayData.total_students ?? 0;
        checkoutReady = total > 0 && (todayData.evening_count ?? 0) / total >= 0.5;
      }
      const visible = fridayEvening ? checkoutReady : weekend;
      const dominant = trends.week.trend !== "no-data" ? trends.week.trend : trends.month.trend !== "no-data" ? trends.month.trend : "stable";
      const recommendationView = trendView[dominant];
      const recommendationText = tipFor(dominant, trends);
      const standDate = new Date(current);
      if (weekday === 6) standDate.setDate(standDate.getDate() - 1);
      if (weekday === 0) standDate.setDate(standDate.getDate() - 2);
      let recommendations = initialRecommendations;
      if (visible) {
        const weekStart = new Date(current);
        weekStart.setDate(weekStart.getDate() - (weekday === 0 ? 6 : weekday - 1));
        await http.request({ path: `/classes/${classId}/weekly-recommendations`, method: "POST", token, body: { week_start: localDateKey(weekStart), trend: dominant, tip_text: recommendationText } }).catch(() => undefined);
        recommendations = await http.request<MoodRecommendation[]>({ path: `/classes/${classId}/weekly-recommendations`, token }).catch(() => initialRecommendations);
      }

      const labels: MoodLevel["label"][] = ["Heute", "Woche", "Monat", "Jahr"];
      const keys: Array<keyof MoodTrendsResponse> = ["day", "week", "month", "year"];
      return {
        period,
        periodLabel: periodLabels[period],
        todayValue: today?.avg_mood ?? null,
        todayCount: today ? today.students.length : null,
        periodValue: mappedTrend ?? calculatedPeriod,
        levels: keys.map((key, index) => {
          const value = trends[key];
          const view = trendView[value.trend];
          const delta = value.current != null && value.previous != null ? value.current - value.previous : null;
          return { ...value, label: labels[index], color: view.color, trendLabel: view.label, delta: delta == null ? "" : `${delta > 0 ? "+" : ""}${delta.toFixed(1)}` };
        }),
        recommendation: {
          visible,
          label: recommendationView.label,
          color: recommendationView.color,
          text: recommendationText,
          standLabel: `Stand: Fr ${String(standDate.getDate()).padStart(2, "0")}.${String(standDate.getMonth() + 1).padStart(2, "0")}.`
        },
        recommendations: recommendations.map((item) => ({ ...item, periodLabel: recommendationPeriodLabel(item.week_start) })),
        labels: history.data.map((day) => `${day.weekday} ${day.date.slice(5).replace("-", ".")}`),
        series: chartSeries(history.data)
      };
    }
  };
}

export type MoodModule = ReturnType<typeof createMoodModule>;
